/*
 * Shared By Opulence product grid builder — used by both the hero
 * landing page (per-collection grids) and the all-products page (one
 * flat grid). Every product renders as a static tile; a product's alt
 * colour/back (data-alt-src) is a second image stacked on the same
 * tile, shown on hover (desktop) or by a short horizontal drag
 * (mobile) — a plain tap still opens the product, only a real swipe
 * previews the other image instead of navigating. A thin segmented
 * line at the bottom of the image hints at the second image before
 * the user touches anything.
 *
 * A 2-colour product also renders as TWO tiles (one defaulting to
 * each colour, each still swipeable to the other) so 2-colour
 * collections don't look sparser than single-colour ones.
 *
 * Scrolling: this file never touches scroll position and never uses
 * ScrollTrigger. Tile reveals are one-shot IntersectionObserver
 * callbacks, so there are no cached pixel positions to go stale and no
 * page "refresh" is ever needed when images/fonts change page height.
 */
(function (global) {
  'use strict';

  function readItem(slide) {
    // A "coming soon" feature slide: a wide tile with a video (or poster)
    // instead of a product photo, a name, and "Coming soon" in place of the
    // price. It is two tiles wide (see .op-grid-tile--feature in the page CSS).
    if (slide.hasAttribute('data-coming-soon')) {
      const link = slide.querySelector('.op-stage-media');
      const lbl = slide.querySelector('.op-stage-label');
      const prc = slide.querySelector('.op-stage-price');
      return {
        comingSoon: true,
        href: link ? link.getAttribute('href') : '#',
        label: lbl ? lbl.textContent : '',
        price: prc ? prc.textContent : 'Coming soon',
        videoDesktop: slide.getAttribute('data-video-desktop') || '',
        videoMobile: slide.getAttribute('data-video-mobile') || '',
        poster: slide.getAttribute('data-poster') || '',
        posterMobile: slide.getAttribute('data-poster-mobile') || '',
      };
    }
    const media = slide.querySelector('.op-stage-media');
    const img = media.querySelector('img');
    const label = slide.querySelector('.op-stage-label');
    const price = slide.querySelector('.op-stage-price');
    return {
      href: media.getAttribute('href'),
      src: img.getAttribute('src'),
      altSrc: img.dataset.altSrc || null,
      alt: img.getAttribute('alt'),
      label: label ? label.textContent : '',
      price: price ? price.textContent : '',
    };
  }

  // Expand each product into its display entries — a 2-colour product
  // becomes two entries (one per default colour), each still able to
  // swipe/hover to the other.
  function expandItems(items) {
    const expanded = [];
    items.forEach((item) => {
      if (item.altSrc) {
        expanded.push({ href: item.href, src: item.src, altSrc: item.altSrc, alt: item.alt, label: item.label, price: item.price });
        expanded.push({ href: item.href, src: item.altSrc, altSrc: item.src, alt: item.alt, label: item.label, price: item.price });
      } else {
        expanded.push(item);
      }
    });
    return expanded;
  }

  function buildFeatureTile(item) {
    const tile = document.createElement('div');
    tile.className = 'op-grid-tile op-grid-tile--feature';

    const mediaLink = document.createElement('a');
    mediaLink.className = 'op-grid-media';
    mediaLink.href = item.href;

    // Pick one file for this screen (a media="" attribute on <source> is
    // ignored by browsers, so choose in script) — phones get the small
    // portrait file, everything else the full-size one.
    const isPhone = window.matchMedia('(max-width: 749px)').matches;
    const src = isPhone ? (item.videoMobile || item.videoDesktop) : (item.videoDesktop || item.videoMobile);
    if (src) {
      const video = document.createElement('video');
      video.className = 'op-stage-img op-stage-img--primary';
      video.muted = true;
      video.loop = true;
      video.autoplay = true;
      video.playsInline = true;
      video.preload = 'auto';
      video.setAttribute('aria-hidden', 'true');
      const posterSrc = isPhone ? (item.posterMobile || item.poster) : item.poster;
      if (posterSrc) video.poster = posterSrc;
      video.src = src;
      mediaLink.appendChild(video);
      const p = video.play();
      if (p && p.catch) p.catch(function () {});
    } else if (item.poster) {
      const img = document.createElement('img');
      img.className = 'op-stage-img op-stage-img--primary';
      img.src = item.poster;
      img.alt = item.label;
      img.loading = 'lazy';
      img.decoding = 'async';
      img.draggable = false;
      mediaLink.appendChild(img);
    }
    tile.appendChild(mediaLink);

    const infoLink = document.createElement('a');
    infoLink.className = 'op-grid-info';
    infoLink.href = item.href;
    const label = document.createElement('span');
    label.className = 'op-grid-label';
    label.textContent = item.label;
    infoLink.appendChild(label);
    const priceRow = document.createElement('span');
    priceRow.className = 'op-grid-price-row';
    const price = document.createElement('span');
    price.className = 'op-grid-price';
    price.textContent = item.price;
    priceRow.appendChild(price);
    infoLink.appendChild(priceRow);
    tile.appendChild(infoLink);
    return tile;
  }

  function buildGridTile(item) {
    if (item.comingSoon) return buildFeatureTile(item);
    const tile = document.createElement('div');
    tile.className = 'op-grid-tile';

    const mediaLink = document.createElement('a');
    mediaLink.className = 'op-grid-media';
    mediaLink.href = item.href;

    const primaryImg = document.createElement('img');
    primaryImg.className = 'op-stage-img op-stage-img--primary';
    primaryImg.src = item.src;
    primaryImg.alt = item.alt;
    primaryImg.loading = 'lazy';
    primaryImg.decoding = 'async';
    primaryImg.draggable = false;
    mediaLink.appendChild(primaryImg);

    if (item.altSrc) {
      const altImg = document.createElement('img');
      altImg.className = 'op-stage-img op-stage-img--alt';
      altImg.src = item.altSrc;
      altImg.alt = item.alt;
      altImg.loading = 'lazy';
      altImg.decoding = 'async';
      altImg.draggable = false;
      mediaLink.appendChild(altImg);

      const track = document.createElement('div');
      track.className = 'op-grid-swipe-track';
      const segA = document.createElement('span');
      segA.className = 'op-grid-swipe-seg is-active';
      const segB = document.createElement('span');
      segB.className = 'op-grid-swipe-seg';
      track.appendChild(segA);
      track.appendChild(segB);
      mediaLink.appendChild(track);

      // Pointer events only ever *observe* the drag; nothing here calls
      // preventDefault on a pointer/touch event, and the tile is
      // touch-action: pan-y, so vertical page scrolling stays native.
      let dragging = false;
      let moved = false;
      let startX = 0;
      mediaLink.addEventListener('pointerdown', (e) => {
        if (e.pointerType === 'mouse') return; // desktop uses :hover instead
        dragging = true;
        moved = false;
        startX = e.clientX;
      }, { passive: true });
      mediaLink.addEventListener('pointermove', (e) => {
        if (!dragging) return;
        const dx = e.clientX - startX;
        if (Math.abs(dx) > 8) moved = true;
        const showAlt = dx < -14;
        altImg.style.opacity = showAlt ? '1' : '0';
        segA.classList.toggle('is-active', !showAlt);
        segB.classList.toggle('is-active', showAlt);
      }, { passive: true });
      function endDrag() { dragging = false; }
      mediaLink.addEventListener('pointerup', endDrag, { passive: true });
      mediaLink.addEventListener('pointercancel', endDrag, { passive: true });
      mediaLink.addEventListener('click', (e) => { if (moved) e.preventDefault(); });
    }

    tile.appendChild(mediaLink);

    const infoLink = document.createElement('a');
    infoLink.className = 'op-grid-info';
    infoLink.href = item.href;

    const label = document.createElement('span');
    label.className = 'op-grid-label';
    label.textContent = item.label;
    infoLink.appendChild(label);

    const priceRow = document.createElement('span');
    priceRow.className = 'op-grid-price-row';
    const price = document.createElement('span');
    price.className = 'op-grid-price';
    price.textContent = item.price;
    priceRow.appendChild(price);

    if (item.altSrc) {
      const dots = document.createElement('span');
      dots.className = 'op-grid-dots';
      [item.src, item.altSrc].forEach((src) => {
        const dot = document.createElement('img');
        dot.className = 'op-grid-dot';
        dot.src = src;
        dot.alt = '';
        dot.loading = 'lazy';
        dot.decoding = 'async';
        dots.appendChild(dot);
      });
      priceRow.appendChild(dots);
    }

    infoLink.appendChild(priceRow);
    tile.appendChild(infoLink);

    return tile;
  }

  // Builds a static grid inside `stage` from its `[data-stage-slide]`
  // source markup. Tiles fade/stagger in once, the first time the stage
  // nears the viewport (IntersectionObserver — also fires for stages
  // already above the viewport after a scroll-restoring reload).
  function buildGrid(stage) {
    const items = Array.from(stage.querySelectorAll('[data-stage-slide]')).map(readItem);
    if (!items.length) return;

    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const displayItems = expandItems(items);

    stage.innerHTML = '';
    const grid = document.createElement('div');
    grid.className = 'op-product-grid';
    stage.appendChild(grid);

    const tiles = displayItems.map((item) => {
      const tile = buildGridTile(item);
      grid.appendChild(tile);
      return tile;
    });

    if (typeof gsap === 'undefined' || prefersReduced) return;

    gsap.set(tiles, { opacity: 0, y: 24 });
    const reveal = function () {
      gsap.to(tiles, { opacity: 1, y: 0, duration: 0.7, stagger: 0.04, ease: 'power2.out' });
    };
    if (!('IntersectionObserver' in window)) { reveal(); return; }
    const io = new IntersectionObserver(function (entries) {
      for (let i = 0; i < entries.length; i++) {
        const e = entries[i];
        if (e.isIntersecting || e.boundingClientRect.top < 0) {
          io.disconnect();
          reveal();
          return;
        }
      }
    }, { rootMargin: '0px 0px -12% 0px', threshold: 0 });
    io.observe(stage);
  }

  function initAll() {
    document.querySelectorAll('[data-stage]').forEach(buildGrid);
  }

  global.OpulenceGrid = { buildGrid: buildGrid, initAll: initAll };
})(window);
